import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react";

type ReadinessContextValue = {
  cycle: number;
  open: boolean;
  register: (token: symbol, key: string) => void;
  unregister: (token: symbol) => void;
  reset: () => void;
};

const ReadinessContext = createContext<ReadinessContextValue | null>(null);

const DEBOUNCE_MS = 120;

export function ReadinessProvider({ children }: PropsWithChildren) {
  const pendingRef = useRef<Map<symbol, string>>(new Map());
  const [pendingVersion, setPendingVersion] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [open, setOpen] = useState(false);

  const bumpPending = useCallback(() => {
    setPendingVersion((current) => current + 1);
  }, []);

  const register = useCallback((token: symbol, key: string) => {
    pendingRef.current.set(token, key);
    bumpPending();
  }, [bumpPending]);

  const unregister = useCallback((token: symbol) => {
    if (pendingRef.current.delete(token)) bumpPending();
  }, [bumpPending]);

  const reset = useCallback(() => {
    pendingRef.current.clear();
    setOpen(false);
    setCycle((current) => current + 1);
    bumpPending();
  }, [bumpPending]);

  useEffect(() => {
    if (open || pendingRef.current.size > 0) return undefined;
    const timer = setTimeout(() => {
      if (pendingRef.current.size === 0) setOpen(true);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [cycle, open, pendingVersion]);

  const value = useMemo<ReadinessContextValue>(
    () => ({ cycle, open, register, unregister, reset }),
    [cycle, open, register, unregister, reset]
  );

  return <ReadinessContext.Provider value={value}>{children}</ReadinessContext.Provider>;
}

export function useReadinessHold(key: string, ready: boolean): void {
  const context = useContext(ReadinessContext);
  if (!context) throw new Error("useReadinessHold requires ReadinessProvider");
  const tokenRef = useRef(Symbol(key));

  useEffect(() => {
    const token = tokenRef.current;
    if (ready || context.open) {
      context.unregister(token);
      return undefined;
    }
    context.register(token, key);
    return () => context.unregister(token);
  }, [context, key, ready]);
}

export function useAppComposed(): boolean {
  const context = useContext(ReadinessContext);
  if (!context) throw new Error("useAppComposed requires ReadinessProvider");
  return context.open;
}

export function useResetReadiness(): () => void {
  const context = useContext(ReadinessContext);
  if (!context) throw new Error("useResetReadiness requires ReadinessProvider");
  return context.reset;
}
